"use client";

import { useEffect, useMemo, useState } from "react";
import { InternalVolumeUnavailable, useInternalVolumeAccess } from "@/lib/useInternalVolumeAccess";
import IndexBadges from "@/components/IndexBadges";

type VolumeRow = {
  symbol: string;
  name: string;
  sector: string;
  date: string;
  close: number;
  change_pct: number;
  volume: number;
  avg_volume_20d: number;
  volume_zscore: number;
  rvol: number;
  traded_value: number;
  anomaly_score: number;
  tag: string;
  signal: string;
  liquidity_score: number;
  indices?: string[];
};

type VolumeResponse = {
  ok: boolean;
  timestamp: string;
  scanned: number;
  count: number;
  rows: VolumeRow[];
};

type SignalFilter = "all" | "bullish" | "bearish" | "neutral";

const formatNumber = (v: number, d = 2) =>
  v.toLocaleString(undefined, { maximumFractionDigits: d });

const formatVolume = (v: number) => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(v);
};

const signalColor = (signal: string) => {
  const s = signal?.toLowerCase() ?? "";
  if (s.includes("bull") || s.includes("accum") || s.includes("break")) return "var(--color-success)";
  if (s.includes("bear") || s.includes("distrib") || s.includes("sell")) return "var(--color-danger)";
  return "#f59e0b";
};

const changeColor = (pct: number) => (pct >= 0 ? "var(--color-success)" : "var(--color-danger)");

export default function VolumeAnomaliesPage() {
  const { allowed, checked, token } = useInternalVolumeAccess();
  const [data, setData] = useState<VolumeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signalFilter, setSignalFilter] = useState<SignalFilter>("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<"rvol" | "volume_zscore" | "anomaly_score" | "traded_value">("anomaly_score");

  useEffect(() => {
    if (!checked || !allowed || !token) return;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch("/api/outputs/volume_abnormal.json", {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as VolumeResponse;
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load volume data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [allowed, checked, token]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const term = search.trim().toUpperCase();
    return data.rows
      .filter((r) => {
        if (term && !r.symbol.includes(term) && !r.name?.toUpperCase().includes(term)) return false;
        if (signalFilter === "all") return true;
        const s = r.signal?.toLowerCase() ?? "";
        if (signalFilter === "bullish") return s.includes("bull") || s.includes("accum") || s.includes("break");
        if (signalFilter === "bearish") return s.includes("bear") || s.includes("distrib") || s.includes("sell");
        return !s.includes("bull") && !s.includes("accum") && !s.includes("bear") && !s.includes("distrib");
      })
      .sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
  }, [data, search, signalFilter, sortKey]);

  const SIGNAL_FILTERS: { value: SignalFilter; label: string }[] = [
    { value: "all", label: "All Signals" },
    { value: "bullish", label: "Bullish" },
    { value: "bearish", label: "Bearish" },
    { value: "neutral", label: "Neutral" },
  ];

  const SORT_OPTIONS: { value: typeof sortKey; label: string }[] = [
    { value: "anomaly_score", label: "Anomaly Score" },
    { value: "rvol", label: "Relative Volume" },
    { value: "volume_zscore", label: "Z-Score" },
    { value: "traded_value", label: "Traded Value" },
  ];

  const scanDate = data?.timestamp
    ? new Date(data.timestamp).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "N/A";

  return (
    !checked ? (
      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <p style={{ color: "var(--color-text-muted)" }}>Checking access...</p>
        </div>
      </section>
    ) : !allowed ? (
      <InternalVolumeUnavailable />
    ) : (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Volume Anomalies</h1>
          <p className="page-subtitle">Stocks with unusual volume activity — elevated z-score and relative volume flag early accumulation or distribution.</p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">

          {/* Summary bar */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "0.75rem",
            marginBottom: "1.5rem",
          }}>
            {[
              { label: "Scan Date", value: scanDate },
              { label: "Scanned", value: data ? formatNumber(data.scanned, 0) : "—" },
              { label: "Anomalies", value: data ? formatNumber(data.count, 0) : "—" },
              { label: "Showing", value: formatNumber(rows.length, 0) },
            ].map((c) => (
              <div key={c.label} style={{
                background: "rgba(15, 23, 42, 0.6)",
                border: "1px solid rgba(148, 163, 184, 0.15)",
                borderRadius: "10px",
                padding: "0.85rem 1rem",
              }}>
                <div style={{ fontSize: "0.75rem", color: "rgba(148,163,184,0.65)", marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{c.label}</div>
                <div style={{ fontWeight: 700, fontSize: "1.1rem" }}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", marginBottom: "1.25rem", alignItems: "center" }}>
            {SIGNAL_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setSignalFilter(f.value)}
                style={{
                  padding: "0.4rem 0.9rem",
                  borderRadius: "999px",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  border: signalFilter === f.value ? "1px solid rgba(59,130,246,0.55)" : "1px solid rgba(148,163,184,0.22)",
                  background: signalFilter === f.value ? "rgba(59,130,246,0.18)" : "rgba(15,23,42,0.5)",
                  color: "var(--color-text)",
                }}
              >
                {f.label}
              </button>
            ))}
            <div style={{ display: "flex", gap: "0.5rem", marginLeft: "auto", flexWrap: "wrap", alignItems: "center" }}>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as typeof sortKey)}
                style={{
                  padding: "0.4rem 0.75rem",
                  borderRadius: "8px",
                  border: "1px solid rgba(148,163,184,0.3)",
                  background: "rgba(15,23,42,0.5)",
                  color: "var(--color-text)",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                }}
              >
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search symbol"
                style={{
                  padding: "0.4rem 0.75rem",
                  borderRadius: "8px",
                  border: "1px solid rgba(148,163,184,0.3)",
                  background: "rgba(15,23,42,0.5)",
                  color: "var(--color-text)",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  width: "150px",
                }}
              />
            </div>
          </div>

          {loading && (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>Loading volume data...</div>
          )}

          {error && (
            <div style={{ padding: "1rem", borderRadius: "12px", border: "1px solid rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.08)", color: "var(--color-danger)", marginBottom: "1rem" }}>
              {error}
            </div>
          )}

          {!loading && !error && (
            <div style={{ overflowX: "auto", borderRadius: "14px", border: "1px solid rgba(148,163,184,0.2)", background: "rgba(15,23,42,0.55)" }}>
              <table className="data-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Symbol", "Sector", "Close", "Chg %", "Volume", "RVol", "Z-Score", "Anomaly", "Signal"].map((h, i) => (
                      <th key={h} style={{
                        padding: "0.85rem 0.85rem",
                        fontSize: "0.78rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        color: "var(--color-text-muted)",
                        textAlign: i >= 2 ? "right" : "left",
                        whiteSpace: "nowrap",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "2rem", color: "var(--color-text-muted)" }}>
                        No stocks match the selected filters.
                      </td>
                    </tr>
                  ) : rows.map((row, idx) => (
                    <tr key={row.symbol} style={{ background: idx % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
                      <td style={{ padding: "0.75rem 0.85rem", fontWeight: 700 }}>
                        <div>
                          {row.symbol}
                          <IndexBadges indices={row.indices ?? []} style={{ marginLeft: 5, verticalAlign: "middle" }} />
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "rgba(148,163,184,0.6)", fontWeight: 400 }}>{row.name}</div>
                      </td>
                      <td style={{ padding: "0.75rem 0.85rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{row.sector || "—"}</td>
                      <td style={{ padding: "0.75rem 0.85rem", textAlign: "right", fontWeight: 600 }}>Rs. {formatNumber(row.close)}</td>
                      <td style={{ padding: "0.75rem 0.85rem", textAlign: "right", fontWeight: 700, color: changeColor(row.change_pct) }}>
                        {row.change_pct >= 0 ? "+" : ""}{row.change_pct?.toFixed(2)}%
                      </td>
                      <td style={{ padding: "0.75rem 0.85rem", textAlign: "right", color: "var(--color-text-muted)" }}>{formatVolume(row.volume)}</td>
                      <td style={{ padding: "0.75rem 0.85rem", textAlign: "right", fontWeight: 700, color: row.rvol >= 3 ? "#f59e0b" : "var(--color-text)" }}>
                        {row.rvol?.toFixed(1)}x
                      </td>
                      <td style={{ padding: "0.75rem 0.85rem", textAlign: "right", color: "var(--color-text-muted)" }}>
                        {row.volume_zscore?.toFixed(1)}
                      </td>
                      <td style={{ padding: "0.75rem 0.85rem", textAlign: "right", fontWeight: 700 }}>
                        {row.anomaly_score?.toFixed(1)}
                      </td>
                      <td style={{ padding: "0.75rem 0.85rem", textAlign: "right" }}>
                        <span style={{
                          display: "inline-block",
                          padding: "0.2rem 0.6rem",
                          borderRadius: "999px",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: signalColor(row.signal),
                          background: `${signalColor(row.signal)}18`,
                          border: `1px solid ${signalColor(row.signal)}35`,
                          whiteSpace: "nowrap",
                        }}>
                          {row.signal || row.tag || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
    )
  );
}
